import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import GenreShow from "./GenreShow";
import Footer from "../../components/Footer/Footer";
import { useHoverBackground } from "../../utils/Actions/useHoverBackground";
import { Helmet } from "react-helmet";
import { useActiveGenre } from "../../utils/Genre/activeGenreState";
import { getGenrePageData } from "../../utils/api/helpers/pages";
import PageLoader from "../../utils/Ui/PageLoader";

gsap.registerPlugin(ScrollTrigger);

export default function Genre() {
  const { genreSlug } = useParams();
  const [genreName, setGenreName] = useState("");
  const [themeColor, setThemeColor] = useState(null);
  const [randIndex, setRandIndex] = useState(0);

  // Split readiness flags
  const [isDataReady, setIsDataReady] = useState(false);

  // Derived loader flag (only false when BOTH are ready)
  const isLoading = !isDataReady;

  const containerRef = useRef(null);
  const featuredImageRef = useRef(null);
  const pageContentRef = useRef(null);
  const [activeGenre, setActiveGenre] = useActiveGenre();

  const hoverProps = useHoverBackground(
    activeGenre?.theme?.colors?.dark?.hex,
    activeGenre?.theme?.colors?.light?.hex,
  );

  const hasAwards = true;

  // Fetch genre data
  useEffect(() => {
    if (!genreSlug) return;

    // Reset readiness on route change
    setIsDataReady(false);
    // setIsHeroReady(false);

    getGenrePageData(genreSlug)
      .then((res) => {
        setGenreName(res.data.title);
        setActiveGenre(res.data);
        setIsDataReady(true);
      })
      .catch(() => {
        // Don't block forever if API fails (optional error UI elsewhere)
        setIsDataReady(true);
      });
  }, [genreSlug, setActiveGenre]);

  // Set theme color and random index
  useEffect(() => {
    if (!activeGenre) return;

    setThemeColor(activeGenre?.theme?.colors?.dark?.rgb);
    // setRandIndex(getRandomIndex(activeGenre?.featuredShows));
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [activeGenre]);

  // GSAP animations
  useGSAP(
    () => {
      if (!activeGenre || isLoading) return;

      const container = containerRef.current;
      const bgImg = featuredImageRef.current;
      if (!container || !bgImg) return;

      const gridItems = container.querySelectorAll(".genre-show");

      if (gridItems.length) {
        gsap.set(gridItems, {
          y: 20,
          opacity: 0,
          filter: "blur(10px)",
        });

        gsap.to(gridItems, {
          y: 0,
          opacity: 1,
          filter: "blur(0px)",
          duration: 1,
          ease: "power3.out",
          stagger: 0.08,
        });
      }

      gsap.to(bgImg, {
        filter: "blur(10px)",
        opacity: 0.5,
        ease: "none",
        scrollTrigger: {
          trigger: container,
          start: "top top",
          end: "bottom bottom",
          scrub: true,
          invalidateOnRefresh: true,
        },
      });

      ScrollTrigger.refresh();
    },
    {
      dependencies: [activeGenre, randIndex, isLoading],
      scope: containerRef,
    },
  );

  // Margin calculation based on featured image size
  useEffect(() => {
    const featuredEl = featuredImageRef.current;
    const pageEl = pageContentRef.current;

    if (!featuredEl || !pageEl) return;
    let rafId = 0;

    const applyMargin = () => {
      const page = pageContentRef.current;
      if (!page) return;

      const imgHeight = featuredEl.offsetHeight * 0.6;
      page.style.marginTop = `${imgHeight}px`;
    };

    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(applyMargin);
    });

    resizeObserver.observe(featuredEl);
    applyMargin();

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [activeGenre, randIndex]);

  return (
    <>
      <PageLoader isLoading={isLoading} />
      <div
        className="page-container genre-container"
        ref={containerRef}
        style={{
          background: `linear-gradient(
            to right,
            rgba(${themeColor},0.7) 0%,
            rgba(0,0,0,1) 50%,
            rgba(${themeColor},0.7) 100%
          )`,
        }}
      >
        {genreName && (
          <Helmet>
            <title>{`${genreName} | FYC`}</title>
            <meta name="description" content={genreName} />
          </Helmet>
        )}

        <div className="page-content" ref={pageContentRef}>
          <div className="width-ctrl">
            <div className="genre-grid" key={genreSlug}>
              {!isLoading &&
                activeGenre &&
                activeGenre?.featuredShows?.map((show, idx) => {
                  return (
                    <GenreShow
                      key={show.slug}
                      idx={idx}
                      genreSlug={genreSlug}
                      activeGenre={activeGenre}
                      show={show}
                      hasAwards={hasAwards}
                      featured={true}
                    />
                  );
                })}
            </div>

            {!isLoading &&
            activeGenre &&
            activeGenre?.unFeaturedShows?.length &&
            !activeGenre?.children.length ? (
              <div className={"subgenre-container"}>
                <h4 className={"subgenre-title"}>{activeGenre?.title}</h4>
                <div className="genre-grid subgenre-grid" key={genreSlug}>
                  {activeGenre?.unFeaturedShows?.map((show, idx) => (
                    <GenreShow
                      key={show.slug}
                      idx={idx}
                      genreSlug={genreSlug}
                      activeGenre={activeGenre}
                      show={show}
                      hasAwards={hasAwards}
                      featured={false}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {!isLoading &&
              activeGenre &&
              activeGenre?.children?.map((genre, idx) => (
                <div key={idx} className={"subgenre-container"}>
                  <h4 className={"subgenre-title"}>{genre.title}</h4>
                  <div className="genre-grid subgenre-grid" key={genreSlug}>
                    {genre.shows &&
                      genre?.shows?.map((show, idx2) => (
                        <GenreShow
                          key={show.slug}
                          idx={idx2}
                          genreSlug={genreSlug}
                          activeGenre={activeGenre}
                          show={show}
                          hasAwards={hasAwards}
                          featured={false}
                        />
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </div>

        <Footer />

        <div
          className={"genre-background " + (activeGenre ? "loaded" : "")}
          style={{
            background: `linear-gradient(
            to right,
            rgba(${themeColor},0.7) 0%,
            rgba(0,0,0,1) 50%,
            rgba(${themeColor},0.7) 100%
          )`,
          }}
          aria-hidden={"true"}
        />
      </div>
    </>
  );
}
